import { create } from "zustand";
import type {
  Product,
  ProductVariant,
  AttributeOption,
  Customer,
  Promotion,
} from "@/types";

export type CartPromotion = Pick<Promotion, "id" | "name" | "type">;

export interface CartItem {
  id: string;
  product: Product;
  variant: ProductVariant;
  quantity: number;
  selectedOptions: AttributeOption[];
  notes: string;
  unitPrice: number;
  subtotal: number;
  isComped: boolean;
  compReason: string;
}

interface CartState {
  items: CartItem[];
  customer: Customer | null;
  promotion: CartPromotion | null;
  discountAmount: number;
  notes: string;
  orderType: "dine_in" | "takeaway";

  getSubtotal: () => number;
  getItemCount: () => number;

  addItem: (
    product: Product,
    variant: ProductVariant,
    quantity: number,
    selectedOptions: AttributeOption[],
    notes?: string,
  ) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  setItemComped: (itemId: string, isComped: boolean, reason?: string) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  setCustomer: (customer: Customer | null) => void;
  setPromotion: (
    promotion: CartPromotion | null,
    discountAmount: number,
  ) => void;
  clearPromotion: () => void;
  setNotes: (notes: string) => void;
  setOrderType: (orderType: "dine_in" | "takeaway") => void;
}

function generateCartItemId(): string {
  return `cart_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function unitPriceWithOptions(
  variant: ProductVariant,
  options: AttributeOption[],
): number {
  return options.reduce(
    (price, option) => price + option.priceAdjustment,
    variant.price,
  );
}

function lineSubtotal(item: {
  unitPrice: number;
  quantity: number;
  isComped: boolean;
}): number {
  if (item.isComped) {
    return 0;
  }

  return item.unitPrice * item.quantity;
}

function sameLine(
  item: CartItem,
  variant: ProductVariant,
  selectedOptions: AttributeOption[],
  notes: string,
): boolean {
  const optionIds = (options: AttributeOption[]) =>
    options
      .map((option) => option.id)
      .sort()
      .join(",");

  return (
    item.variant.id === variant.id &&
    item.notes === notes &&
    optionIds(item.selectedOptions) === optionIds(selectedOptions)
  );
}

function withLineSubtotal(item: CartItem): CartItem {
  return { ...item, subtotal: lineSubtotal(item) };
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  customer: null,
  promotion: null,
  discountAmount: 0,
  notes: "",
  orderType: "dine_in",

  getSubtotal: () =>
    get().items.reduce((sum, item) => sum + item.subtotal, 0),

  getItemCount: () =>
    get().items.reduce((sum, item) => sum + item.quantity, 0),

  addItem: (product, variant, quantity, selectedOptions, notes = "") => {
    const existing = get().items.find((item) =>
      sameLine(item, variant, selectedOptions, notes),
    );

    if (existing) {
      set({
        items: get().items.map((item) =>
          item.id === existing.id
            ? withLineSubtotal({
                ...item,
                quantity: item.quantity + quantity,
              })
            : item,
        ),
      });

      return;
    }

    const newItem: CartItem = withLineSubtotal({
      id: generateCartItemId(),
      product,
      variant,
      quantity,
      selectedOptions,
      notes,
      unitPrice: unitPriceWithOptions(variant, selectedOptions),
      subtotal: 0,
      isComped: false,
      compReason: "",
    });

    set({ items: [...get().items, newItem] });
  },

  updateItemQuantity: (itemId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(itemId);

      return;
    }

    set({
      items: get().items.map((item) =>
        item.id === itemId ? withLineSubtotal({ ...item, quantity }) : item,
      ),
    });
  },

  setItemComped: (itemId, isComped, reason = "") => {
    set({
      items: get().items.map((item) =>
        item.id === itemId
          ? withLineSubtotal({
              ...item,
              isComped,
              compReason: isComped ? reason : "",
            })
          : item,
      ),
    });
  },

  removeItem: (itemId) => {
    set({ items: get().items.filter((item) => item.id !== itemId) });
  },

  clearCart: () => {
    set({
      items: [],
      customer: null,
      promotion: null,
      discountAmount: 0,
      notes: "",
    });
  },

  setCustomer: (customer) => {
    set({ customer });
  },

  setPromotion: (promotion, discountAmount) => {
    set({ promotion, discountAmount });
  },

  clearPromotion: () => {
    set({ promotion: null, discountAmount: 0 });
  },

  setNotes: (notes) => {
    set({ notes });
  },

  setOrderType: (orderType) => {
    set({ orderType });
  },
}));

export const selectCartItems = (state: CartState) => state.items;
export const selectCartCustomer = (state: CartState) => state.customer;
export const selectCartPromotion = (state: CartState) => state.promotion;
export const selectCartDiscountAmount = (state: CartState) =>
  state.discountAmount;
export const selectCartNotes = (state: CartState) => state.notes;
export const selectCartOrderType = (state: CartState) => state.orderType;
